/*
 * -----------------------------------------------------------------------
 *  <copyright file="common.ts" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import { gql } from '@apollo/client';
import {
    CommentFragment,
    CommentsWithDeletedItemsFragment,
    CommentWithDeletedRepliesFragment,
    GridStateFragment,
    NamedItemFragment,
    NamedItemFragmentLOV } from './fragments';

export const GET_NAMED_ITEMS_BY_TYPE = gql`
    query getNamedItemsByType($type: NamedItemType!, $includeSystemItems: Boolean) {
        namedItemsByType(type: $type, includeSystemItems: $includeSystemItems) {
            ...NamedItemFields
        }
    }
    ${NamedItemFragment}
`;

export const GET_NAMED_ITEMS_BY_TYPE_FOR_STUDY = gql`
    query getNamedItemsByTypeForStudy($studyId: ObjectId!, $type: NamedItemType!, $includeSystemItems: Boolean) {
        namedItemsByTypeForStudy(studyId: $studyId, type: $type, includeSystemItems: $includeSystemItems) {
            ...NamedItemFields
        }
    }
    ${NamedItemFragment}
`;

export const SAVE_NAMED_ITEM = gql`
    mutation saveNamedItem($id: ObjectId, $type: NamedItemType!, $name: String!, $comments: String, $active: Boolean, $studyId: ObjectId!) {
        saveNamedItem(id: $id, type: $type, name: $name, comments: $comments, active: $active, studyId: $studyId) {
            ...NamedItemFields
        }
    }
    ${NamedItemFragment}
`;

export const GET_NAMED_ITEMS = gql`
    query getNamedItems($includeSystemItems: Boolean, $includeStudyItems: Boolean) {
        namedItems(includeSystemItems: $includeSystemItems, includeStudyItems: $includeStudyItems) {
            ...NamedItemFieldsLOV
        }
    }
    ${NamedItemFragmentLOV}
`;

export const GET_GRID_STATUS = gql`
    query getGridStatus($gridId: Int!) {
        gridStates(gridId: $gridId) {
            ...GridStateFields
        }
    }
    ${GridStateFragment}
`;

export const UPSERT_GRID_STATE = gql`
    mutation updateGridState(
        $gridId: Int
        $gridState: String
        $id: ObjectId
        $isFavourite: Boolean
        $name: String
    ) {
        updateGridState(
            gridId: $gridId
            gridStateData: $gridState
            id: $id
            isFavourite: $isFavourite
            name: $name
        ) {
            ...GridStateFields
        }
    }
    ${GridStateFragment}
`;

export const DELETE_GRID_STATE = gql`
mutation deleteGridState($id: ObjectId!) {
    deleteGridState(id: $id)
}`;

export const GET_COMMENTS = gql`
    query getComments($commentsInput: CommentsInput!) {
        comments(comments: $commentsInput) {
            ...CommentFields
        }
    }
    ${CommentFragment}
`;

export const GET_UPDATED_COMMENTS = gql`
    query getUpdatedComments($updatedCommentsInput: UpdatedCommentsInput!) {
        updatedComments(comments: $updatedCommentsInput) {
            ...CommentsWithDeletedItemsFields
        }
    }
    ${CommentsWithDeletedItemsFragment}
`;

export const GET_UPDATED_COMMENTS_BY_ID = gql`
    query getUpdatedCommentsById($updatedCommentsByIdInput: UpdatedCommentsByIdInput!) {
        updatedCommentsById(comments: $updatedCommentsByIdInput) {
            ...CommentsWithDeletedItemsFields
        }
    }
    ${CommentsWithDeletedItemsFragment}
`;

export const CREATE_COMMENT = gql`
    mutation createComment($commentInput: CreateCommentInput!) {
        createComment(comment: $commentInput) {
            ...CommentFields
        }
    }
    ${CommentFragment}
`;

export const UPDATE_COMMENT = gql`
    mutation updateComment($commentInput: UpdateCommentInput!) {
        updateComment(comment: $commentInput) {
            ...CommentWithDeletedRepliesFields
        }
    }
    ${CommentWithDeletedRepliesFragment}
`;

export const DELETE_COMMENT  = gql`
    mutation deleteComment($commentInput: DeleteCommentInput!) {
        deleteComment(comment: $commentInput)
    }
`;

export const OPEN_COMMENT  = gql`
    mutation openComment($commentInput: OpenCommentInput!) {
        openComment(comment: $commentInput) {
            ...CommentWithDeletedRepliesFields
        }
    }
    ${CommentWithDeletedRepliesFragment}
`;

export const CLOSE_COMMENT  = gql`
    mutation closeComment($commentInput: CloseCommentInput!) {
        closeComment(comment: $commentInput) {
            ...CommentWithDeletedRepliesFields
        }
    }
    ${CommentWithDeletedRepliesFragment}
`;

export const CREATE_COMMENT_REPLY = gql`
    mutation createCommentReply($replyInput: CreateCommentReplyInput!) {
        createCommentReply(reply: $replyInput) {
            ...CommentWithDeletedRepliesFields
        }
    }
    ${CommentWithDeletedRepliesFragment}
`;

export const DELETE_COMMENT_REPLY  = gql`
    mutation deleteCommentReply($replyInput: DeleteCommentReplyInput!) {
        deleteCommentReply(reply: $replyInput) {
            ...CommentWithDeletedRepliesFields
        }
    }
    ${CommentWithDeletedRepliesFragment}
`;

export const UPDATE_COMMENT_REPLY  = gql`
    mutation updateCommentReply($replyInput: UpdateCommentReplyInput!) {
        updateCommentReply(reply: $replyInput) {
            ...CommentWithDeletedRepliesFields
        }
    }
    ${CommentWithDeletedRepliesFragment}
`;

